import React from 'react';
import { useTranslation } from 'react-i18next';

import { Sheet, useSheetDismiss } from '@/nova/components/Sheet';
import { Button, Text } from '@/nova/components';
import { Body, DangerButton, FooterRow } from './CancelInvoiceModal.styles';

export type CancelInvoiceModalProps = {
  open: boolean;
  /** Short invoice reference (e.g. "467D8BC7"), interpolated into the body copy. */
  number: string;
  /** Pre-formatted invoice amount (e.g. "$59.99"), interpolated into the body copy. */
  amount: string;
  /** Cancel request in flight — disables the actions and blocks a double submit. */
  busy?: boolean;
  onClose: () => void;
  /** Confirm cancellation — calls the `invoice/cancel` endpoint (terminal for the invoice). */
  onConfirm: () => void;
};

/**
 * Confirmation dialog for cancelling a pending invoice. Cancelling is terminal (the
 * invoice can no longer be paid or restored), so it asks first. Mirrors the
 * product-detail `DeactivateModal`: a centred Overlay with an outline dismiss and a
 * solid red confirm CTA. `onConfirm` calls the `invoice/cancel` endpoint.
 */
export const CancelInvoiceModal = ({
  open,
  number,
  amount,
  busy = false,
  onClose,
  onConfirm,
}: CancelInvoiceModalProps): React.ReactElement | null => {
  const { t } = useTranslation();
  // Dismiss through the overlay so the dialog slides out; wired straight to onClose it
  // would unmount on the spot and blink away.
  const { closeHandle, dismiss } = useSheetDismiss(onClose);

  if (!open) {
    return null;
  }

  return (
    <Sheet
      // The Overlay re-runs the title through t() (a no-op on an already-resolved
      // string); wrapping it here is what makes the key extractable (prop literals
      // aren't harvested — see docs/i18n.md).
      title={t('Cancel this invoice?')}
      onClose={onClose}
      closeHandle={closeHandle}
      footer={
        <FooterRow>
          <Button variant="outline" label={t('Keep invoice')} disabled={busy} onClick={dismiss} />
          <DangerButton type="button" disabled={busy} onClick={onConfirm}>
            {/* Literal `t()` calls so both labels are harvested (a dynamic `text={…}`
                expression is not extracted — see docs/i18n.md). */}
            <Text text={busy ? t('Cancelling…') : t('Cancel invoice')} tag="span" />
          </DangerButton>
        </FooterRow>
      }
    >
      <Body
        text="Invoice #{{number}} for {{amount}} will be cancelled. This can't be undone, and you won't be charged."
        placeholders={{ number, amount }}
      />
    </Sheet>
  );
};

export default CancelInvoiceModal;
